import { tutorialActions, TutorialMessage } from '@/store/reducers/tutorialSlice';
import { RootState } from '@/store/store';
import { Store } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { getCurrentBearerToken } from '../client-auth';

class TutorialService {
    private static instance: TutorialService;
    private store: any; // Redux store reference

    private previouslySeenFragments: string[];
    private tutorialsDisabled: boolean;

    private dismissedMessages: TutorialMessage[];

    private constructor() {
      this.previouslySeenFragments = [];
      this.tutorialsDisabled = false;
      this.dismissedMessages = [];
    }
    
    static getInstance(): TutorialService {
      if (!TutorialService.instance) {
        TutorialService.instance = new TutorialService();
      }
      return TutorialService.instance;
    }
    
    init(store: Store<RootState>) {
      this.store = store;
    }
    
    queueMessage(message: Omit<TutorialMessage, 'id'>) {
      // Skip if the message is already in the queue
      if (this.store.getState().tutorial.messageQueue.find(m => m.fragmentId === message.fragmentId)) {
        return;
      }

      this.store.dispatch(tutorialActions.queueMessage({
        ...message,
        id: uuidv4()
      }));
    }
    
    dismiss(message: TutorialMessage = null) {
      this.store.dispatch(tutorialActions.dismissCurrent());

      if (message) {
        this.dismissedMessages.push(message);
      }
    }

    clearModalIdFragmentFromQueue(modalId: string) {
      const filteredQueue = this.store.getState().tutorial.messageQueue.filter(m => m.modalId !== modalId);
      this.store.dispatch(tutorialActions.setMessageQueue(filteredQueue));
    }

    async loadData() {
        const response = await fetch('/api/user/tutorial/seen', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken(),
            },
        });
        const data = await response.json() as { fragmentsSeen: string[], tutorialsDisabled: boolean };
        this.previouslySeenFragments = data.fragmentsSeen ?? [];
        this.tutorialsDisabled = data.tutorialsDisabled ?? false;        
    }

    async resetSeenData(): Promise<boolean> {
      const response = await fetch("/api/user/tutorial/seen", {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getCurrentBearerToken(),
        },
      });
      
      const data = await response.json();

      if (data.success) {
        this.previouslySeenFragments = [];
        this.tutorialsDisabled = false;
      }

      return true;
    }

    async markFragmentAsSeen(fragmentId: string, language: string): Promise<boolean> {
        const response = await fetch('/api/user/tutorial/seen', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken(),
            },
            body: JSON.stringify({ fragmentId, language }),
        });
        const data = await response.json() as { success: boolean };
        if (data.success) {
          this.previouslySeenFragments.push(fragmentId);
        }
        return data.success;
    }

    isFragmentSeen(fragmentId: string): boolean {
      return this.previouslySeenFragments.includes(fragmentId);
    }

    isTutorialsDisabled(): boolean {
      return this.tutorialsDisabled;
    }

    messageContextMatches(message: TutorialMessage, currentPath: string): boolean {
      let match = false;

      if (!message.contextPath || message.contextPath.length === 0) {
        match = true;
      } else {
        match = message.contextPath.some(path => currentPath.includes(path));
      }

      return match;
    }
  }
  
  export const appTutorialService = TutorialService.getInstance();